import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { checkAuthStatus, logout as authLogout } from '../services/authService';
import { commonUtil } from '../services/commonUtil';
import envService from '../services/envService';
import { ConfigOdsData, getActiveConfig } from '../services/configOdsService';
import { getUserRegisterId } from '../utils/userUtils';

export interface User {
  sub: string;
  pid: string;
  family_name: string;
  family_name_en: string;
  given_name: string;
  given_name_en: string;
  title: string;
  title_en: string;
  email: string;
}

export interface UserRegisterStore {
  USER_REGISTER_ID: string;
  USER_REGISTER_TYPECODE: string;
  USER_REGISTER_COMPANY_NAME_TH: string;
  USER_REGISTER_COMPANY_NAME_EN: string;
  USER_REGISTER_POSITION_TH: string;
  USER_REGISTER_POSITION_EN: string;
  USER_REGISTER_WORKLOCATION: string;
  USER_REGISTER_WORKPHONE: string;
  USER_REGISTER_WORKPHONE_EXT: string;
  USER_REGISTER_USERNAME: string;
  USER_REGISTER_PASSWORD: string;
  USER_REGISTER_EMAIL: string;
  USER_REGISTER_PREFIX_TH: string;
  USER_REGISTER_PREFIX_EN: string;
  USER_REGISTER_FIRSTNAME_TH: string;
  USER_REGISTER_FIRSTNAME_EN: string;
  USER_REGISTER_LASTNAME_TH: string;
  USER_REGISTER_LASTNAME_EN: string;
  USER_REGISTER_IDCARD: string;
  USER_REGISTER_MOBILEPHONE: string;
  USER_REGISTER_OBJECTIVE: string;
  USER_REGISTER_STATUS: string;
  USER_REGISTER_DATE: string;
  USER_REGISTER_REASON: string;
  USER_REGISTER_START_DATE: string;
  USER_REGISTER_END_DATE: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  userRegisterStore: UserRegisterStore[] | null;
  loading: boolean;
  error: string | null;
  configData: ConfigOdsData | null;
  configLoading: boolean;
  configError: string | null;
  userSuspended: boolean;
  suspensionLoading: boolean;
  login: (userData: UserRegisterStore[]) => void;
  logout: () => Promise<void>;
  setIsAuthenticated: (value: boolean) => void;
  setUser: (user: User) => void;
  clearUser: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string) => void;
  fetchUserRegister: (user: any) => Promise<boolean>;
  fetchUserRegisterBypass: (user: any) => Promise<boolean>;
  refreshConfig: () => Promise<void>;
  checkUserSuspensionStatus: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [userRegisterStore, setUserRegisterStore] = useState<UserRegisterStore[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [configData, setConfigData] = useState<ConfigOdsData | null>(null);
  const [configLoading, setConfigLoading] = useState<boolean>(false);
  const [configError, setConfigError] = useState<string | null>(null);
  const [userSuspended, setUserSuspended] = useState<boolean>(false);
  const [suspensionLoading, setSuspensionLoading] = useState<boolean>(false);

  useEffect(() => {
    const initializeAuth = async () => {
      const storedUserRegister = localStorage.getItem("userregister");

      if (storedUserRegister) {
        setIsAuthenticated(true);
        setUserRegisterStore(JSON.parse(storedUserRegister));
        console.log("userRegisterStore", JSON.parse(storedUserRegister));
        setLoading(false);
        return;
      }

      // const authResponse = await checkAuthStatus();
      // setIsAuthenticated(authResponse.isAuthenticated);
      // if (authResponse.isAuthenticated && authResponse.user) {
      //   setUser(authResponse.user);
      // }
      setLoading(false);
    };

    initializeAuth();
  }, []);

  const login = (userData: UserRegisterStore[]) => {
    localStorage.setItem("userregister", JSON.stringify(userData));
    setUserRegisterStore(userData);
    setIsAuthenticated(true);
    setError(null);
  };

  const logout = async () => {
    try {
      await authLogout();

      // Clear localStorage
      localStorage.removeItem("userregister");
      localStorage.clear();

      // Clear all cookies
      document.cookie.split(";").forEach((c) => {
        const eqPos = c.indexOf("=");
        const name = eqPos > -1 ? c.substr(0, eqPos) : c;
        document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
        document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=" + window.location.hostname;
        document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=." + window.location.hostname;
      });

      // Clear sessionStorage
      sessionStorage.clear();

      setIsAuthenticated(false);
      setUserRegisterStore(null);
      setUser(null);
      setError(null);

      // Redirect to login page
      window.location.href = envService.getPublicUrl() + '/login';
    } catch (error) {
      console.error('Logout failed:', error);
      setError('Logout failed');
    }
  };

  const clearUser = () => {
    setIsAuthenticated(false);
    setUser(null);
    setUserRegisterStore(null);
    setLoading(false);
    setError(null);
    localStorage.removeItem("userregister")
  };

  const fetchUserRegister = async (userTemp: any): Promise<boolean> => {
    if (userTemp == null) {
      userTemp = user;
    }
    let personalCardId = "";
    try {
      const userRegisterFromStorage = JSON.parse(localStorage.getItem("userregister") || "null");
      if (userRegisterFromStorage && userRegisterFromStorage[0]?.PERSONAL_ID) {
        personalCardId = userRegisterFromStorage[0].PERSONAL_ID;
      }
    } catch (error) {
      console.error("Error parsing localStorage.userregister:", error);
    }

    if (!personalCardId && !userTemp?.pid) {
      console.error("No user PID available");
      return false;
    }

    const idCard = userTemp?.pid || personalCardId;
    const API_BASE_URL = envService.getApiBaseUrl() || 'http://localhost:8081/ods-service';
    const apiUrl = `${API_BASE_URL}/api/user-register/idcard/${idCard}`;

    setLoading(true);
    try {
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();
      console.log("Received Response:", responseData);

      if (responseData.success && responseData.data && responseData.data.userRegister) {
        // Check if user_register_approve_status is 2
        if (responseData.data.userRegister.user_register_approve_status === 2) {
          // Convert the response data to match UserRegisterStore format
          const userRegisters: UserRegisterStore[] = [{
            USER_REGISTER_ID: responseData.data.userRegister.user_register_id,
            USER_REGISTER_TYPECODE: responseData.data.userRegister.user_register_typecode,
            USER_REGISTER_COMPANY_NAME_TH: responseData.data.userRegister.user_register_company_name_th,
            USER_REGISTER_COMPANY_NAME_EN: responseData.data.userRegister.user_register_company_name_en,
            USER_REGISTER_POSITION_TH: responseData.data.userRegister.user_register_position_th,
            USER_REGISTER_POSITION_EN: responseData.data.userRegister.user_register_position_en,
            USER_REGISTER_WORKLOCATION: responseData.data.userRegister.user_register_worklocation,
            USER_REGISTER_WORKPHONE: responseData.data.userRegister.user_register_workphone,
            USER_REGISTER_WORKPHONE_EXT: responseData.data.userRegister.user_register_workphone_ext,
            USER_REGISTER_USERNAME: responseData.data.userRegister.user_register_username,
            USER_REGISTER_PASSWORD: responseData.data.userRegister.user_register_password,
            USER_REGISTER_EMAIL: responseData.data.userRegister.user_register_email,
            USER_REGISTER_PREFIX_TH: responseData.data.userRegister.user_register_prefix_th,
            USER_REGISTER_PREFIX_EN: responseData.data.userRegister.user_register_prefix_en,
            USER_REGISTER_FIRSTNAME_TH: responseData.data.userRegister.user_register_firstname_th,
            USER_REGISTER_FIRSTNAME_EN: responseData.data.userRegister.user_register_firstname_en,
            USER_REGISTER_LASTNAME_TH: responseData.data.userRegister.user_register_lastname_th,
            USER_REGISTER_LASTNAME_EN: responseData.data.userRegister.user_register_lastname_en,
            USER_REGISTER_IDCARD: responseData.data.userRegister.user_register_idcard,
            USER_REGISTER_MOBILEPHONE: responseData.data.userRegister.user_register_mobilephone,
            USER_REGISTER_OBJECTIVE: responseData.data.userRegister.user_register_objective,
            USER_REGISTER_STATUS: responseData.data.userRegister.user_register_status,
            USER_REGISTER_DATE: responseData.data.userRegister.user_register_date,
            USER_REGISTER_REASON: responseData.data.userRegister.user_register_reason,
            USER_REGISTER_START_DATE: responseData.data.userRegister.user_register_start_date,
            USER_REGISTER_END_DATE: responseData.data.userRegister.user_register_end_date,
          }];

          setUserRegisterStore(userRegisters);
          if (userRegisters) {
            localStorage.setItem("userregister", JSON.stringify(userRegisters));
            return true;
          }
        } else {
          console.log("User registration not approved. Status:", responseData.data.userRegister.user_register_approve_status);
          return false;
        }
      }
    } catch (error) {
      console.error("Error in fetchUserRegister:", error);
    } finally {
      setLoading(false);
    }
    return false;
  };


  const fetchUserRegisterBypass = async (userTemp: any): Promise<boolean> => {
    if (userTemp == null) {
      userTemp = user;
    }

    if (!userTemp?.pid) {
      console.error("No user PID available");
      return false;
    }

    const idCard = userTemp?.pid;
    const API_BASE_URL = envService.getApiBaseUrl() || 'http://localhost:8081/ods-service';
    const apiUrl = `${API_BASE_URL}/api/user-register/idcard/${idCard}`;

    setLoading(true);
    try {
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();
      console.log("Received Response:", responseData);

      if (responseData.success && responseData.data && responseData.data.userRegister) {
        // Check if user_register_approve_status is 2
        if (responseData.data.userRegister.user_register_approve_status === 2) {
          // Convert the response data to match UserRegisterStore format
          const userRegisters: UserRegisterStore[] = [{
            USER_REGISTER_ID: responseData.data.userRegister.user_register_id,
            USER_REGISTER_TYPECODE: responseData.data.userRegister.user_register_typecode,
            USER_REGISTER_COMPANY_NAME_TH: responseData.data.userRegister.user_register_company_name_th,
            USER_REGISTER_COMPANY_NAME_EN: responseData.data.userRegister.user_register_company_name_en,
            USER_REGISTER_POSITION_TH: responseData.data.userRegister.user_register_position_th,
            USER_REGISTER_POSITION_EN: responseData.data.userRegister.user_register_position_en,
            USER_REGISTER_WORKLOCATION: responseData.data.userRegister.user_register_worklocation,
            USER_REGISTER_WORKPHONE: responseData.data.userRegister.user_register_workphone,
            USER_REGISTER_WORKPHONE_EXT: responseData.data.userRegister.user_register_workphone_ext,
            USER_REGISTER_USERNAME: responseData.data.userRegister.user_register_username,
            USER_REGISTER_PASSWORD: responseData.data.userRegister.user_register_password,
            USER_REGISTER_EMAIL: responseData.data.userRegister.user_register_email,
            USER_REGISTER_PREFIX_TH: responseData.data.userRegister.user_register_prefix_th,
            USER_REGISTER_PREFIX_EN: responseData.data.userRegister.user_register_prefix_en,
            USER_REGISTER_FIRSTNAME_TH: responseData.data.userRegister.user_register_firstname_th,
            USER_REGISTER_FIRSTNAME_EN: responseData.data.userRegister.user_register_firstname_en,
            USER_REGISTER_LASTNAME_TH: responseData.data.userRegister.user_register_lastname_th,
            USER_REGISTER_LASTNAME_EN: responseData.data.userRegister.user_register_lastname_en,
            USER_REGISTER_IDCARD: responseData.data.userRegister.user_register_idcard,
            USER_REGISTER_MOBILEPHONE: responseData.data.userRegister.user_register_mobilephone,
            USER_REGISTER_OBJECTIVE: responseData.data.userRegister.user_register_objective,
            USER_REGISTER_STATUS: responseData.data.userRegister.user_register_status,
            USER_REGISTER_DATE: responseData.data.userRegister.user_register_date,
            USER_REGISTER_REASON: responseData.data.userRegister.user_register_reason,
            USER_REGISTER_START_DATE: responseData.data.userRegister.user_register_start_date,
            USER_REGISTER_END_DATE: responseData.data.userRegister.user_register_end_date,
          }];

          setUserRegisterStore(userRegisters);
          if (userRegisters) {
            localStorage.setItem("userregister", JSON.stringify(userRegisters));
            return true;
          }
        } else {
          let user: User = {
            pid: responseData.data.userRegister.user_register_idcard,
            sub: responseData.data.userRegister.user_register_idcard,
            family_name: responseData.data.userRegister.user_register_lastname_th,
            family_name_en: responseData.data.userRegister.user_register_lastname_en,
            given_name: responseData.data.userRegister.user_register_firstname_th,
            given_name_en: responseData.data.userRegister.user_register_firstname_en,
            title: responseData.data.userRegister.user_register_prefix_th,
            title_en: responseData.data.userRegister.user_register_prefix_en,
            email: responseData.data.userRegister.user_register_email
          }
          setUser(user);
          console.log("User registration not approved. Status:", responseData.data.userRegister.user_register_approve_status);
          return false;
        }
      } else
        return false;
    } catch (error) {
      console.error("Error in fetchUserRegisterBypass:", error);
    } finally {
      setLoading(false);
    }
    return false;
  };

  const refreshConfig = async (): Promise<void> => {
    try {
      setConfigLoading(true);
      setConfigError(null);
      const data = await getActiveConfig();
      setConfigData(data);
      console.log('Config ODS data loaded in AuthProvider:', data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch config data';
      setConfigError(errorMessage);
      console.error('Failed to fetch config data in AuthProvider:', err);
    } finally {
      setConfigLoading(false);
    }
  };

  // Check user suspension status
  const checkUserSuspensionStatus = async (): Promise<void> => {
    try {
      setSuspensionLoading(true);
      const userRegisterId = getUserRegisterId();
      const API_BASE_URL = envService.getApiBaseUrl() || 'http://localhost:8081/ods-service';
      const apiUrl = `${API_BASE_URL}/api/user-register/${userRegisterId}/detail`;

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        const result = await response.json();
        console.log('User detail response:', result);
        
        // Check if user is suspended - API returns data in nested structure
        const userData = result.data || result;
        if (userData.user_register_detail_status === 'SUSPENDED') {
          console.log('User is SUSPENDED, setting userSuspended to true');
          setUserSuspended(true);
        } else {
          console.log('User is not suspended, status:', userData.user_register_detail_status);
          setUserSuspended(false);
        }
      }
    } catch (err) {
      console.error('Error checking user suspension status:', err);
      // Don't set error state for this check, just log it
    } finally {
      setSuspensionLoading(false);
    }
  };

  // Fetch config data and check suspension status when component mounts
  useEffect(() => {
    refreshConfig();
    if (isAuthenticated) {
      checkUserSuspensionStatus();
    }
  }, [isAuthenticated]);

  const value: AuthContextType = {
    isAuthenticated,
    user,
    userRegisterStore,
    loading,
    error,
    configData,
    configLoading,
    configError,
    userSuspended,
    suspensionLoading,
    login,
    logout,
    setIsAuthenticated,
    setUser,
    clearUser,
    setLoading,
    setError,
    fetchUserRegister,
    fetchUserRegisterBypass,
    refreshConfig,
    checkUserSuspensionStatus,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
